import { useEffect, useMemo, useState } from "react";
import api from "../auth/api";

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString();
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function fmtTime(hora) {
  if (!hora) return "";
  const s = String(hora);
  const m = s.match(/^(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : s.slice(0, 8);
}

export default function BitacoraSearchPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [contrato, setContrato] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [contestar, setContestar] = useState("");
  const [order, setOrder] = useState("desc");

  const [page, setPage] = useState(1);
  const limit = 10;
  const [total, setTotal] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const canQuery = useMemo(() => {
    return (
      !!String(contrato || "").trim() ||
      !!String(q || "").trim() ||
      !!String(dateFrom || "").trim() ||
      !!String(dateTo || "").trim() ||
      contestar === "0" ||
      contestar === "1"
    );
  }, [contrato, q, dateFrom, dateTo, contestar]);

  async function fetchRows() {
    setLoading(true);
    setErr("");

    try {
      const { data } = await api.get("/novedades", {
        params: {
          contrato: String(contrato || "").trim() || undefined,
          q: String(q || "").trim() || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          contestar: contestar === "" ? undefined : contestar,
          order: order || "desc",
          page,
          pageSize: limit,
        },
      });

      setRows(data?.rows || []);
      setTotal(Number(data?.pagination?.total || 0));
    } catch (e) {
      console.error("fetchRows", e);
      setRows([]);
      setTotal(0);
      setErr(e?.response?.data?.error || e?.message || "No se pudo consultar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(() => {
      fetchRows();
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, contrato, q, dateFrom, dateTo, contestar, order]);

  function resetFilters() {
    setContrato("");
    setQ("");
    setDateFrom("");
    setDateTo("");
    setContestar("");
    setOrder("desc");
    setPage(1);
  }

  return (
    <div className="bit-root">
      <style>{css}</style>

      <div className="page-header">
        <div>
          <h1 className="page-title">Búsqueda de Bitácoras</h1>
          <p className="page-subtitle">
            Consulta novedades por contrato, texto, fechas y estado de contestación.
          </p>
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-grid">
          <div className="field">
            <label>Contrato</label>
            <input
              className="input"
              value={contrato}
              onChange={(e) => {
                setContrato(e.target.value.replace(/[^\d]/g, ""));
                setPage(1);
              }}
              placeholder="Ej: 39774"
            />
          </div>

          <div className="field">
            <label>Buscar texto</label>
            <input
              className="input"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Ej: pago, visita, reclamo..."
            />
          </div>

          <div className="field">
            <label>Desde</label>
            <input
              type="date"
              className="input"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="field">
            <label>Hasta</label>
            <input
              type="date"
              className="input"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="field">
            <label>Contestar</label>
            <select
              className="input"
              value={contestar}
              onChange={(e) => {
                setContestar(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="1">Sí</option>
              <option value="0">No</option>
            </select>
          </div>

          <div className="field">
            <label>Orden</label>
            <select
              className="input"
              value={order}
              onChange={(e) => {
                setOrder(e.target.value);
                setPage(1);
              }}
            >
              <option value="desc">Más reciente</option>
              <option value="asc">Más antiguo</option>
            </select>
          </div>
        </div>

        <div className="filters-actions">
          <button className="btn-secondary" onClick={resetFilters}>
            Limpiar filtros
          </button>
          <button className="btn-primary" onClick={fetchRows} disabled={loading}>
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {!canQuery && (
          <div className="hint">
            Puedes buscar por contrato, por texto o combinar ambos.
          </div>
        )}

        {err ? <div className="error-box">{err}</div> : null}
      </div>

      <div className="table-card">
        <div className="table-head">
          <div>
            Resultados: <b>{total}</b>
          </div>
          <div>
            Página <b>{page}</b> de <b>{totalPages}</b>
          </div>
        </div>

        <div className="table-wrap">
          {loading ? (
            <div className="empty">Cargando bitácoras…</div>
          ) : rows.length ? (
            <table>
              <thead>
                <tr>
                  <th>Contrato</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Origen</th>
                  <th>Contestar</th>
                  <th>Novedad</th>
                  <th>Usuario</th>
                  <th>Urgencia</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${idx}_${r.contrato}_${r.fecha}_${r.hora}`}>
                    <td>
                      <b>{r.contrato || "—"}</b>
                    </td>
                    <td>{fmtDate(r.fecha)}</td>
                    <td>{fmtTime(r.hora)}</td>
                    <td>{r.origen || "—"}</td>
                    <td>
                      <span
                        className={cls(
                          "pill",
                          Number(r.contestar) ? "ok" : "neutral"
                        )}
                      >
                        {Number(r.contestar) ? "Sí" : "No"}
                      </span>
                    </td>
                    <td style={{ minWidth: 320 }}>
                      <div className="novedad-cell">{r.novedad || "—"}</div>
                    </td>
                    <td>{r.usuario || "—"}</td>
                    <td>
                      <span
                        className={cls(
                          "pill",
                          Number(r.urgencia) ? "danger" : "neutral"
                        )}
                      >
                        {Number(r.urgencia) ? "Sí" : "No"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty">No hay resultados.</div>
          )}
        </div>

        <div className="pager-wrap">
          <div className="pager">
            <button
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ◀ Anterior
            </button>

            <span>
              Página {page} de {totalPages}
            </span>

            <button
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente ▶
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const css = `
.bit-root{
  height:auto;
  padding:12px;
  background:#0b1220;
  color:#e5e7eb;
  display:grid;
  gap:12px;
}

.page-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
}

.page-title{
  margin:0;
  font-size:24px;
  font-weight:800;
}

.page-subtitle{
  margin:4px 0 0;
  color:#94a3b8;
  font-size:13px;
}

.filters-card{
  background:#0f172a;
  border:1px solid #1e293b;
  border-radius:14px;
  padding:14px;
  display:grid;
  gap:12px;
}

.filters-grid{
  display:grid;
  grid-template-columns: repeat(6, minmax(0,1fr));
  gap:12px;
}

.field{
  display:grid;
  gap:6px;
}

.field label{
  font-size:12px;
  color:#94a3b8;
  font-weight:700;
}

.input{
  width:100%;
  background:#111827;
  border:1px solid #1e293b;
  border-radius:10px;
  padding:10px;
  color:white;
}

.input:focus{
  outline:none;
  border-color:#3b82f6;
  box-shadow:0 0 0 3px rgba(59,130,246,.12);
}

.filters-actions{
  display:flex;
  justify-content:flex-end;
  gap:10px;
}

.btn-primary{
  background:#2563eb;
  border:none;
  color:white;
  padding:10px 14px;
  border-radius:10px;
  font-weight:800;
  cursor:pointer;
}

.btn-secondary{
  background:#1f2937;
  border:1px solid #374151;
  color:white;
  padding:10px 14px;
  border-radius:10px;
  font-weight:700;
  cursor:pointer;
}

.hint{
  color:#94a3b8;
  font-size:12px;
}

.error-box{
  background:#7f1d1d;
  border:1px solid #b91c1c;
  color:#fecaca;
  padding:10px 12px;
  border-radius:10px;
  font-size:13px;
}

.table-card{
  background:#0f172a;
  border:1px solid #1e293b;
  border-radius:14px;
  overflow:hidden;
}

.table-head{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  padding:12px 14px;
  border-bottom:1px solid #1e293b;
  color:#94a3b8;
  font-size:13px;
}

.table-head b{
  color:#e5e7eb;
}

.table-wrap{
  overflow:auto;
}

table{
  width:100%;
  border-collapse:collapse;
}

th, td{
  padding:10px;
  border-bottom:1px solid #1e293b;
  text-align:left;
  vertical-align:top;
}

th{
  font-size:12px;
  color:#94a3b8;
}

.empty{
  text-align:center;
  padding:30px;
  color:#94a3b8;
}

.novedad-cell{
  white-space:pre-wrap;
  min-width:280px;
}

.pill{
  display:inline-flex;
  align-items:center;
  border-radius:999px;
  padding:4px 10px;
  font-size:11px;
  font-weight:800;
  border:1px solid transparent;
}

.pill.ok{
  background:rgba(34,197,94,.16);
  border-color:rgba(34,197,94,.35);
  color:#bbf7d0;
}

.pill.neutral{
  background:rgba(148,163,184,.12);
  border-color:rgba(148,163,184,.25);
  color:#cbd5e1;
}

.pill.danger{
  background:rgba(239,68,68,.14);
  border-color:rgba(239,68,68,.28);
  color:#fecaca;
}

.pager-wrap{
  background:#020617;
  border-top:1px solid #1e293b;
  padding:12px;
  display:flex;
  justify-content:center;
}

.pager{
  display:flex;
  align-items:center;
  gap:10px;
}

.pager button{
  background:linear-gradient(180deg,#1e293b,#020617);
  border:1px solid #1e293b;
  color:#e5e7eb;
  padding:7px 16px;
  border-radius:10px;
  cursor:pointer;
  font-weight:600;
}

.pager button:hover:not(:disabled){
  background:#1e3a8a;
  border-color:#3b82f6;
}

.pager button:disabled{
  opacity:.35;
  cursor:not-allowed;
}

.pager span{
  background:#020617;
  border:1px solid #1e293b;
  padding:7px 14px;
  border-radius:10px;
  color:#94a3b8;
  font-size:13px;
  font-weight:600;
}

@media (max-width: 1200px){
  .filters-grid{
    grid-template-columns: repeat(3, minmax(0,1fr));
  }
}

@media (max-width: 768px){
  .filters-grid{
    grid-template-columns: 1fr;
  }

  .filters-actions,
  .table-head{
    flex-direction:column;
    align-items:stretch;
  }
}
`;